// src/renderer/App.tsx
import { ThemeProvider } from '@pata-app/themes';
import { AuthProvider } from '@pata-app/ui-auth';
import { LoginModalProvider } from '@pata-app/ui-login-modal';

import { ResourcesConfig } from 'aws-amplify';
import { getCurrentUser } from 'aws-amplify/auth';
import React, { PropsWithChildren } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { StaticRouter } from 'react-router-dom/server';

import { endpoints, request } from '../lib/api';
import { getEnv } from '../lib/utils/getEnv';
import AboutUs from '../pages/about-us/aboutUs';
import { BusinessFeatures } from '../pages/businessFeatures/businessFeatures';
import { BusinessGroomer } from '../pages/businessGroomer/businessGroomer';
import { BusinessHome } from '../pages/businessHome/businessHome';
import { BusinessHydrotherapy } from '../pages/businessHydrotherapy/businessHydrotherapy';
import { BusinessMessageTherapy } from '../pages/businessMassageTherapy/businessMassageTherapy';
import { BusinessPataPay } from '../pages/businessPataPay/businessPataPay';
import BusinessPataVsSavvy from '../pages/businessPataVsSavvy/businessPataVsSavvy';
import BusinessPataVsTuft from '../pages/businessPataVsTuft/businessPataVsTuft';
import { BusinessTrainer } from '../pages/businessTrainers/businessTrainers';
import ContactUs from '../pages/contactUs/contactUs';
import { ErrorPage } from '../pages/error/error';
import Home from '../pages/home/home';
import PrivacyPolicy from '../pages/privacyPolicy/privacyPolicy';
import TermsAndConditions from '../pages/termsAndConditions/termsAndConditions';
import '../styles/fonts.css';
import { appTheme, GlobalStyles } from '../styles/theme';

const {
  VITE_AWS_COGNITO_USER_POOL_ID,
  VITE_AWS_COGNITO_USER_POOL_WEB_CLIENT_ID,
  VITE_AWS_COGNITO_IDENTITY_POOL_ID,
  VITE_AWS_COGNITO_DOMAIN,
  VITE_WEB_BASE_URL,
  VITE_API_USER_ROOT_URL,
  VITE_RECAPTCHA_SITE_KEY,
} = getEnv();

// import other pages...

interface AppProps {
  location?: string; // present only on server, undefined on client
}

let hostname = '';

if (typeof window !== 'undefined') {
  hostname = new URL(window.location.href).origin;
}

const amplifyConfig: ResourcesConfig = {
  Auth: {
    Cognito: {
      userPoolId: VITE_AWS_COGNITO_USER_POOL_ID || '',
      userPoolClientId: VITE_AWS_COGNITO_USER_POOL_WEB_CLIENT_ID || '',
      identityPoolId: VITE_AWS_COGNITO_IDENTITY_POOL_ID || '',
      loginWith: {
        oauth: {
          domain: VITE_AWS_COGNITO_DOMAIN || '',
          scopes: ['email', 'profile', 'openid'],
          redirectSignIn: [`${hostname}`],
          redirectSignOut: [`${hostname}/`],
          responseType: 'code',
        },
        username: true,
      },
    },
  },
};

const isServer = typeof window === 'undefined';

const RouterWrapper: React.FC<{ location?: string; children: React.ReactNode }> = ({ location, children }) => {
  if (isServer) {
    return <StaticRouter location={location || '/'}>{children}</StaticRouter>;
  }

  return <BrowserRouter>{children}</BrowserRouter>;
};

const LoginModalWrapper = ({ children }: PropsWithChildren) => {
  const getUser = async (email: string) => {
    const response = await request({ endpoint: endpoints.userDetails(email), method: 'GET' });
    return response.data.data?.userType ?? null;
  };

  const getUserDetails = async () => {
    const user = await getCurrentUser();

    const response = await request({ endpoint: endpoints.user(user.userId), method: 'GET', authHeaders: true });

    const responseData = response?.data?.data;

    if (responseData) {
      return { ...responseData, mobile: responseData.phoneNumber };
    }
  };

  const updateUserDetails = async (data: { firstName: string; lastName?: string; mobile: string }) => {
    const user = await getCurrentUser();

    await request({
      endpoint: endpoints.user(user.userId),
      method: 'PATCH',
      body: {
        id: user.userId,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.mobile,
      },
      authHeaders: true,
    });
  };

  if (isServer) {
    return <>{children}</>;
  }

  return (
    <LoginModalProvider
      recaptchaKey={VITE_RECAPTCHA_SITE_KEY}
      webBaseUrl={VITE_WEB_BASE_URL}
      autoSignInOnRegister={true}
      getUserDetails={getUserDetails}
      updateUserDetails={updateUserDetails}
      getUser={getUser}
    >
      {children}
    </LoginModalProvider>
  );
};

const AuthWrapper: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  if (isServer) {
    return <>{children}</>;
  }
  return (
    <AuthProvider baseMainApiUrl={VITE_API_USER_ROOT_URL} amplifyConfig={amplifyConfig}>
      {children}
    </AuthProvider>
  );
};

const App: React.FC<AppProps> = ({ location }) => {
  const queryClient = new QueryClient();

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={appTheme}>
          <GlobalStyles />

          <RouterWrapper location={location}>
            <AuthWrapper>
              <LoginModalWrapper>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/business" element={<BusinessHome />} />
                  <Route path="/business/pet-grooming" element={<BusinessGroomer />} />
                  <Route path="/business/dog-training" element={<BusinessTrainer />} />
                  <Route path="/business/canine-hydrotherapy" element={<BusinessHydrotherapy />} />
                  <Route path="/business/pet-massage-therapy" element={<BusinessMessageTherapy />} />
                  <Route path="/business/pata-pay" element={<BusinessPataPay />} />
                  <Route path="/business/features" element={<BusinessFeatures />} />
                  <Route path="/business/compare/tuft" element={<BusinessPataVsTuft />} />
                  <Route path="/business/compare/savvy" element={<BusinessPataVsSavvy />} />
                  <Route path="/terms" element={<TermsAndConditions />} />
                  <Route path="/about-us" element={<AboutUs />} />
                  <Route path="/contact-us" element={<ContactUs />} />
                  <Route path="/error" element={<ErrorPage />} />
                  <Route path="/not-found" element={<ErrorPage errorMessage="Not Hound!" />} />
                </Routes>
              </LoginModalWrapper>
            </AuthWrapper>
          </RouterWrapper>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
